import { format } from "date-fns";
import { getCardLastFour, getCardNicknameDisplay, isCreditCard } from "@/components/organisms/PaymentSettings/helpers";
import { extrasLabel, formatDuration } from "@/components/pages/Client/booking/vehicleTransfer";
import { NOT_FLYING_LABEL } from "@/helpers/bookingHelpers";
import { ClientPaymentMethod } from "@/redux/apis/clientPayment/types";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { formatAud } from "@/utils/formatAud";
import { passengerContactKey, transferRateKind } from "./bookingPayload";
import { BookingPassengerDetail, ClientBookingExtra, ClientBookingState, PassengerContact, SELF_PASSENGER_ID } from "./types";

/** One changed record in the "Save Changes to This Booking?" review table. */
export interface BookingDiffRow {
  /** Stable identity for React keys (e.g. "pickup", "stop-1"). */
  key: string;
  /** Human-readable record name shown in the table ("Pickup Location", "Vehicle", …). */
  label: string;
  /** Original value, rendered for display. */
  from: string;
  /** New value, rendered for display. */
  to: string;
}

const EMPTY = "—";

const poiLabel = (poi: PointsOfInterest | null | undefined): string => poi?.address || poi?.city || EMPTY;

const dateLabel = (iso: string | null | undefined): string => {
  if (!iso) return EMPTY;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : format(d, "d MMMM yyyy");
};

const timeLabel = (iso: string | null | undefined): string => {
  if (!iso) return EMPTY;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? EMPTY : format(d, "h:mm a");
};

const dateCmp = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : format(d, "yyyy-MM-dd");
};

const timeCmp = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : format(d, "HH:mm");
};

const passengerContact = (s: Partial<ClientBookingState>): { isSelf: boolean; contact?: PassengerContact } =>
  !s.passenger || s.passenger.kind === "self"
    ? { isSelf: true, contact: s.passengerContactOverrides?.[SELF_PASSENGER_ID] }
    : { isSelf: false, contact: s.passenger };

const passengerLabel = (s: Partial<ClientBookingState>): string => {
  const { isSelf, contact } = passengerContact(s);
  const name = [contact?.title, contact?.firstName, contact?.lastName].filter(Boolean).join(" ");
  const label = [name, contact?.email, contact?.phone].filter(Boolean).join(", ");
  return label || (isSelf ? "Yourself" : "Guest");
};

const passengerCmp = (s: Partial<ClientBookingState>): string => {
  const { isSelf, contact } = passengerContact(s);
  return `${isSelf ? "self" : s.passenger?.kind}:${passengerContactKey(contact)}`;
};

const paymentLabel = (pm: ClientPaymentMethod | null | undefined): string => {
  if (!pm) return EMPTY;
  if (!isCreditCard(pm)) return "Company Invoicing";
  const last4 = getCardLastFour(pm.card_data);
  return `${getCardNicknameDisplay(pm)}${last4 ? ` ••• ${last4}` : ""}`;
};

// Quantity is part of the identity here: without it, going from one baby seat to two reads as no
// change at all, and the edit never reaches the PUT.
const extrasCmp = (extras: ClientBookingExtra[] | undefined): string =>
  (extras ?? [])
    .map((e) => `${e.extra_id}x${e.quantity ?? 1}`)
    .sort()
    .join(",");

/** Additional passengers = rows 1..N (row 0 is the main passenger, covered by the "Passenger" row). */
const extraPassengerRows = (passengers: BookingPassengerDetail[] | undefined): BookingPassengerDetail[] =>
  (passengers ?? []).slice(1).filter((p) => p.name?.trim());

const passengerDetailsCmp = (passengers: BookingPassengerDetail[] | undefined): string =>
  extraPassengerRows(passengers)
    .map((p) => `${p.name.trim()}|${p.phone?.trim() ?? ""}`)
    .join(",");

const passengerDetailsLabel = (passengers: BookingPassengerDetail[] | undefined): string => {
  const names = extraPassengerRows(passengers).map((p) => p.name.trim());
  return names.length ? names.join(", ") : "None";
};

/**
 * A single comparable record: `cmp` decides whether the value changed (raw identity — uuid, id set,
 * yyyy-MM-dd, …); `display` is what the review table renders. Keeping them separate lets us compare on
 * stable ids while still showing friendly labels (e.g. payment compares by card, shows the nickname).
 */
type Field = {
  key: string;
  label: string;
  cmp: (s: Partial<ClientBookingState>) => string;
  display: (s: Partial<ClientBookingState>) => string;
};

const FIELDS: Field[] = [
  { key: "pickup", label: "Pickup Location", cmp: (s) => poiLabel(s.pickup), display: (s) => poiLabel(s.pickup) },
  { key: "destination", label: "Destination", cmp: (s) => poiLabel(s.destination), display: (s) => poiLabel(s.destination) },
  { key: "passenger", label: "Passenger", cmp: passengerCmp, display: passengerLabel },
  { key: "date", label: "Pickup Date", cmp: (s) => dateCmp(s.pickupDatetime), display: (s) => dateLabel(s.pickupDatetime) },
  { key: "time", label: "Pickup Time", cmp: (s) => timeCmp(s.pickupDatetime), display: (s) => timeLabel(s.pickupDatetime) },
  {
    key: "flight",
    label: "Flight Number",
    cmp: (s) => s.pickupFlightNumber || (s.pickupNotFlying ? NOT_FLYING_LABEL : ""),
    display: (s) => s.pickupFlightNumber || (s.pickupNotFlying ? NOT_FLYING_LABEL : EMPTY),
  },
  // The DEPARTURE flight is its own row rather than a widening of the one above: a booking can carry
  // both, and "Flight Number: QF1 → QF994" would report a change to the arrival flight that never happened.
  {
    key: "departureFlight",
    label: "Departure Flight",
    cmp: (s) => s.departureFlightNumber ?? "",
    display: (s) => s.departureFlightNumber || EMPTY,
  },
  { key: "transfer", label: "Transfer Type", cmp: (s) => String(s.transferType?.id ?? ""), display: (s) => s.transferType?.label || EMPTY },
  // How long the car is hired for — hourly and daily rates only, where it drives the fare. Compared as
  // "" for journey trips so switching between two journey types isn't reported as a duration change.
  //
  // Without this row, changing an hourly booking from 2 hours to 4 produced NO diff at all: the
  // transfer type is the same, so "Update Booking" stayed disabled and the change couldn't be saved,
  // even though the backend prices on it.
  {
    key: "duration",
    label: "Duration",
    cmp: (s) => (transferRateKind(s.jobRate) !== "journey" ? String(s.bookedForDuration ?? 0) : ""),
    display: (s) => (transferRateKind(s.jobRate) !== "journey" ? formatDuration(s.bookedForDuration) || EMPTY : EMPTY),
  },
  // The operator's price override, now available for any transfer type (set in the Price Summary). No
  // override ⇒ cmp is "", so it only surfaces as a change when a price was set, cleared, or changed.
  {
    key: "fixedPrice",
    label: "Price Override",
    cmp: (s) => (s.fixedPrice != null ? String(s.fixedPrice) : ""),
    display: (s) => (s.fixedPrice != null ? formatAud(s.fixedPrice) : EMPTY),
  },
  { key: "vehicle", label: "Vehicle", cmp: (s) => String(s.vehicle?.vehicle_id ?? ""), display: (s) => s.vehicle?.name || EMPTY },
  { key: "extras", label: "Extras", cmp: (s) => extrasCmp(s.extras), display: (s) => extrasLabel(s.extras) || "None" },
  {
    key: "passengers",
    label: "Number of Passengers",
    cmp: (s) => String(s.numOfPassengers ?? 1),
    display: (s) => String(s.numOfPassengers ?? 1),
  },
  {
    key: "passengerDetails",
    label: "Additional Passengers",
    cmp: (s) => passengerDetailsCmp(s.passengers),
    display: (s) => passengerDetailsLabel(s.passengers),
  },
  { key: "bags", label: "Number of Bags", cmp: (s) => String(s.numOfBags ?? 0), display: (s) => String(s.numOfBags ?? 0) },
  { key: "payment", label: "Payment", cmp: (s) => s.paymentMethod?.uuid ?? "", display: (s) => paymentLabel(s.paymentMethod) },
  { key: "bookedBy", label: "Booked By", cmp: (s) => s.bookedBy?.trim() ?? "", display: (s) => s.bookedBy?.trim() || EMPTY },
  { key: "reference", label: "Reference", cmp: (s) => s.reference?.trim() ?? "", display: (s) => s.reference?.trim() || EMPTY },
  {
    key: "additionalInfo",
    label: "Notes to Driver",
    cmp: (s) => s.additionalInfo?.trim() ?? "",
    display: (s) => s.additionalInfo?.trim() || EMPTY,
  },
];

/** Diff the ordered intermediate stops into "Stop N" rows (handles added/removed stops). */
const stopRows = (original: Partial<ClientBookingState>, current: Partial<ClientBookingState>): BookingDiffRow[] => {
  const from = original.stops ?? [];
  const to = current.stops ?? [];
  const rows: BookingDiffRow[] = [];
  for (let i = 0; i < Math.max(from.length, to.length); i += 1) {
    const a = poiLabel(from[i]);
    const b = poiLabel(to[i]);
    if (a !== b) rows.push({ key: `stop-${i}`, label: `Stop ${i + 1}`, from: a, to: b });
  }
  return rows;
};

/**
 * Diff keys whose change re-prices the booking, mirroring BookingService::hasPricingRelevantFieldsChanged.
 *
 * Kept beside the diff it reads so the two can't drift: if a field is added to FIELDS and belongs
 * here, it's one edit, not two in different files.
 *
 * Deliberately absent — the backend doesn't compare them either: passenger, additional passengers,
 * flight number, bags, booked by, reference, notes to driver, attachments.
 *
 * `jobRate` needs no entry: VehicleTransferPanel is its only writer and always sets it alongside the
 * transfer type, so "transfer" already covers every way it can change.
 */
const PRICING_DIFF_KEYS = new Set([
  "pickup",
  "destination",
  "date",
  "time",
  "transfer",
  "duration",
  "vehicle",
  "extras",
  "passengers",
  "payment",
  "fixedPrice",
]);

/**
 * Whether any of these diff rows changes what the booking costs.
 *
 * Covers everything the backend prices on: duration has its own row, and `jobRate` can only change
 * via the transfer type, so a diff is sufficient on its own.
 */
export const hasPricingChange = (rows: BookingDiffRow[]) =>
  rows.some((row) => PRICING_DIFF_KEYS.has(row.key) || row.key.startsWith("stop-"));

/**
 * Build the change-review rows for the "Save Changes to This Booking?" modal — one row per record
 * that differs between the booking AS HYDRATED (`original`, the `editOriginal` snapshot) and the live
 * draft (`current`). Unchanged records are omitted ("Show only records that have been updated"), so
 * `buildBookingDiff(...).length` doubles as the "has an updated record" gate for the Update CTA.
 */
export const buildBookingDiff = (
  original: Partial<ClientBookingState> | null | undefined,
  current: ClientBookingState
): BookingDiffRow[] => {
  if (!original) return [];
  const rows: BookingDiffRow[] = [];
  // Pickup + destination first, then any changed stops slot between them (mirrors the trip order).
  for (const field of FIELDS) {
    if (field.cmp(original) !== field.cmp(current)) {
      rows.push({ key: field.key, label: field.label, from: field.display(original), to: field.display(current) });
    }
    if (field.key === "destination") rows.push(...stopRows(original, current));
  }
  return rows;
};
